<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div class="card-body">
            <!-- 상태는 메뉴(라우트)가 정한다 — 入库列表 = 출고대기, 待客户确认 = 출고확인대기(사이드바 별도 메뉴, 2026-10-04 YZY) -->
            <!-- 필터 -->
            <div class="d-flex flex-wrap align-items-center mb-3" style="gap:8px">
              <label class="mb-0 small">入库日期(입고일)</label>
              <input type="date" v-model="startdate" class="form-control form-control-sm" style="width:150px">
              <span>~</span>
              <input type="date" v-model="enddate" class="form-control form-control-sm" style="width:150px">
              <label class="mb-0 small ml-2">货主(업체)</label>
              <input type="text" v-model.trim="companyid" list="inboundCompanyList" placeholder="全部(전체)"
                     class="form-control form-control-sm" style="width:160px" @keyup.enter="search">
              <datalist id="inboundCompanyList">
                <option v-for="c in companyOptions" :key="c" :value="c"></option>
              </datalist>
              <b-button size="sm" variant="primary" @click="search" :disabled="loading">搜索<small>(검색)</small></b-button>
              <b-button size="sm" variant="success" @click="downloadExcel" :disabled="!visibleRows.length">⬇ Excel<small>(엑셀 다운로드)</small></b-button>
              <!-- 蓝胶带快递·黑胶带托盘 박스 → 쿠팡 입고요청·밀크런 엑셀 자동 채움 + 입고ID(2026-10-06 YZY) -->
              <b-button v-if="status === '출고대기'" size="sm" variant="outline-primary" @click="showCoupangInbound = true" :disabled="loading">
                🚚 Coupang入库资料<small>(쿠팡 입고자료)</small>
              </b-button>
              <span class="small text-muted">共 {{ visibleRows.length }}<template v-if="visibleRows.length !== scopedRows.length"> / {{ scopedRows.length }}</template>件</span>
            </div>

            <!-- 待客户确认 은 보낸 직원(作业人)별로 나눠 본다(2026-10-16 崔凡 #48) — 내 건이 있는 계정은 기본으로 내 건만, 끄면 전체 -->
            <div v-if="isConfirm" class="mb-2">
              <b-form-checkbox v-model="mineOnly" switch>
                只看我发送的<small>(내가 보낸 건만)</small> <small class="text-muted" v-if="me">— {{ me }}</small>
                <small class="text-muted">· 全部 {{ rows.length }}件 / 我的 {{ mineCount }}件</small>
              </b-form-checkbox>
            </div>

            <!-- 2단 분류: 1단 清关方式 → 2단 出库形式(테이프 5종). 묶음이 둘 이상이면 표 안에서 그룹 머리말로 묶어 보인다(2026-10-04 YZY) -->
            <div class="d-flex flex-wrap align-items-center mb-1" style="gap:6px">
              <label class="mb-0 small" style="width:110px">清关方式(통관방식)</label>
              <button type="button" class="btn btn-sm" :class="customsFilter === '' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setCustoms('')">
                全部(전체) <span class="badge badge-light">{{ scopedRows.length }}</span>
              </button>
              <button v-for="g in customsGroups" :key="g.key" type="button" class="btn btn-sm"
                      :class="customsFilter === g.key ? 'btn-primary' : 'btn-outline-primary'" @click="setCustoms(g.key)">
                {{ g.text }} <span class="badge badge-light">{{ g.count }}</span>
              </button>
            </div>
            <div class="d-flex flex-wrap align-items-center mb-2" style="gap:6px">
              <label class="mb-0 small" style="width:110px">出库形式(출고형식)</label>
              <button type="button" class="btn btn-sm" :class="outboundFilter === '' ? 'btn-secondary' : 'btn-outline-secondary'" @click="outboundFilter = ''">
                全部(전체) <span class="badge badge-light">{{ customsRows.length }}</span>
              </button>
              <button v-for="g in outboundGroups" :key="g.key" type="button" class="btn btn-sm"
                      :class="g.key === OTHER ? (outboundFilter === g.key ? 'btn-dark' : 'btn-outline-dark') : ''"
                      :style="g.key === OTHER ? {} : outboundTypeStyle(g.key, outboundFilter !== g.key)" @click="outboundFilter = g.key">
                {{ g.text }} <span class="badge badge-light">{{ g.count }}</span>
              </button>
            </div>

            <!-- 탭별 일괄 작업 -->
            <div v-if="status === '출고확인대기'" class="mb-2">
              <b-button size="sm" variant="warning" :disabled="!selectedIds.length || loading" @click="confirmOutbound">
                确认出库(출고확인) → 入库列表(입고리스트) {{ selectedIds.length }}件
              </b-button>
            </div>
            <div v-if="status === '재고대기'" class="mb-2 small text-muted">
              输入库位号后点「确认上架」即进入中国库存(위치번호를 넣고 「재고확정」을 누르면 중국재고로 넘어갑니다).
              <b-button size="sm" variant="info" class="ml-2" :disabled="!selectedIds.length || loading" @click="confirmStockSelected">
                确认上架(재고확정) {{ selectedIds.length }}件
              </b-button>
            </div>
            <!-- 위치번호 라벨 인쇄 + 스캔 재고확정(상품 → 위치) -->
            <location-label-scan v-if="status === '재고대기'" @confirmed="search"></location-label-scan>
            <coupang-inbound-modal v-if="showCoupangInbound" :show="true" :rows="rows"
                                   @close="showCoupangInbound = false" @saved="search"></coupang-inbound-modal>

            <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>

            <div v-else class="table-responsive inbound-list">
              <table class="table table-sm table-bordered table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th v-if="selectable"><input type="checkbox" :checked="allChecked" @change="toggleAll($event.target.checked)"></th>
                    <th v-if="status === '재고대기'">库位号(위치번호)</th>
                    <!-- 待客户确认 은 YZY 가 정한 순서의 열만 보인다(2026-10-04 YZY). 엑셀 양식 열 중 이 목록에 없는 것은 이 화면에서 숨긴다 -->
                    <template v-if="isConfirm">
                      <th v-for="c in confirmCols" :key="c.key" class="inbound-list__th" :title="c.label">{{ c.label }}</th>
                    </template>
                    <!-- 제목 행은 한 줄(긴 제목은 말줄임, 마우스를 올리면 전체) — 엑셀 양식 제목은 그대로 -->
                    <template v-else>
                      <th v-for="(h, hi) in headers" :key="'h' + hi" class="inbound-list__th" :title="h">{{ h.replace(/\n/g, ' ') }}</th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="g in groupedRows">
                  <tr v-if="g.head" :key="'g-' + g.key" class="inbound-list__group">
                    <td :colspan="headers.length + 10">
                      {{ g.customsText }} ›
                      <span class="badge" :style="outboundTypeStyle(g.outKey)">{{ g.outText }}</span>
                      <span class="text-muted ml-1">{{ g.rows.length }}件</span>
                    </td>
                  </tr>
                  <tr v-for="r in g.rows" :key="r._id" :id="'confirm-row-' + r._id" :class="{ 'table-warning': focusId === r._id }">
                    <td v-if="selectable"><input type="checkbox" v-model="r._selected"></td>
                    <td v-if="status === '재고대기'" style="min-width:170px">
                      <div class="d-flex">
                        <input type="text" v-model.trim="r._위치번호" class="form-control form-control-sm" placeholder="A-01-01"
                               @keyup.enter="confirmStock([r])">
                        <b-button size="sm" variant="outline-info" class="ml-1" :disabled="loading" @click="confirmStock([r])">确认</b-button>
                      </div>
                    </td>
                    <!-- 입고처리 「待客户确认」 로 넘어온 건 — 열 순서는 confirmCols 와 같다(2026-10-04 YZY) -->
                    <template v-if="isConfirm">
                      <td class="inbound-list__td">{{ r._cells[COL.업체] }}<div v-if="isSampleSend(r)" class="small text-info" :title="sampleAddrText(r)">🧪 寄客户: {{ sampleAddrText(r) || '地址见其他要求(기타요청사항 참조)' }}</div></td>
                      <td class="inbound-list__td">
                        <img v-if="r._cells[IMG_URL_COL]" :src="r._cells[IMG_URL_COL]" class="inbound-list__img" alt="">
                      </td>
                      <td class="inbound-list__td">{{ r._cells[COL.옵션] }}</td>
                      <td class="inbound-list__td">{{ r._cells[COL.바코드] }}</td>
                      <td class="inbound-list__td">{{ r._cells[COL.상품명] }}</td>
                      <td class="inbound-list__td">{{ r._cells[COL.구매수량] }}</td>
                      <td class="inbound-list__td">{{ r._cells[COL.작업요청] }}</td>
                      <!-- 作业人 = 입고처리에서 待客户确认 으로 보낸 직원(이름·代号) -->
                      <td class="inbound-list__td">{{ staffLabel(r._sentBy, r._sentByCode) }}</td>
                      <td class="inbound-list__td">{{ (r.요청내역 && r.요청내역.workMemo) || '' }}</td>
                      <!-- 韩文翻译 = 需跟客户确认 을 바탕으로 화주에게 보낼 한국어 안내 문안(번역이 아니라 무엇을 확인해 달라는지까지). 2026-10-04 YZY -->
                      <td class="inbound-list__td inbound-list__notice">
                        <div v-if="r.요청내역 && r.요청내역.workMemo_ko" class="inbound-list__notice-text">{{ r.요청내역.workMemo_ko }}</div>
                        <div class="d-flex flex-wrap mt-1" style="gap:4px">
                          <b-button size="sm" variant="outline-info" :disabled="!!r._noticeBusy || !(r.요청내역 && r.요청내역.workMemo && r.요청내역.workMemo.trim())"
                                    @click="makeNotice(r)">
                            <b-spinner v-if="r._noticeBusy" small></b-spinner> 🇰🇷 韩文翻译<small>(문안 생성)</small>
                          </b-button>
                          <b-button v-if="r.요청내역 && r.요청내역.workMemo_ko" size="sm" variant="outline-secondary" @click="copyNotice(r)">📋 复制<small>(복사)</small></b-button>
                        </div>
                      </td>
                      <!-- 处理状态 는 고르는 즉시 저장 → 处理人 자동 기록(2026-10-04 YZY). 客户回复 는 保存 버튼 그대로 -->
                      <td class="inbound-list__td">
                        <b-form-select v-model="r._confirmStatus" :options="confirmStatusOptions" size="sm" style="width:130px"
                                       :disabled="!!r._confirmBusy" @change="saveConfirm(r, true)"></b-form-select>
                      </td>
                      <td class="inbound-list__td inbound-list__reply">
                        <b-form-textarea v-model="r._confirmReply" size="sm" rows="2" max-rows="5"
                                         placeholder="客户怎么说的(화주 회신 내용)"></b-form-textarea>
                        <b-button size="sm" variant="primary" class="mt-1" :disabled="!!r._confirmBusy || !confirmDirty(r)" @click="saveConfirm(r)">
                          <b-spinner v-if="r._confirmBusy" small></b-spinner> 保存<small>(저장)</small>
                        </b-button>
                      </td>
                      <td class="inbound-list__td">
                        {{ staffLabel(r.요청내역 && r.요청내역.confirmHandledBy, r.요청내역 && r.요청내역.confirmHandledByCode) }}
                        <div v-if="r.요청내역 && r.요청내역.confirmHandledAt" class="text-muted">{{ fmtTime(r.요청내역.confirmHandledAt) }}</div>
                      </td>
                      <!-- 목록 밖 두 칸(실제 추가작업·확인사진)은 지우지 않고 맨 뒤로 -->
                      <td class="inbound-list__td">{{ (r.요청내역 && r.요청내역.workMemoActual) || '' }}</td>
                      <td class="inbound-list__td">
                        <div class="d-flex flex-wrap" style="gap:2px;max-width:150px">
                          <a v-for="u in (r._확인사진 || [])" :key="u" :href="u" target="_blank" rel="noopener"><img :src="u" class="inbound-list__img" alt=""></a>
                        </div>
                      </td>
                    </template>
                    <template v-else>
                      <td v-for="(v, ci) in r._cells" :key="'c' + ci" class="inbound-list__td">
                        <img v-if="ci === IMG_COL && r._cells[IMG_URL_COL]" :src="r._cells[IMG_URL_COL]" class="inbound-list__img" alt="">
                        <a v-else-if="ci === URL_COL && v" :href="v" target="_blank" rel="noopener">link</a>
                        <a v-else-if="ci === IMG_URL_COL && v" :href="v" target="_blank" rel="noopener">link</a>
                        <template v-else>{{ v }}<template v-if="ci === COL.업체"><div v-if="isSampleSend(r)" class="small text-info" :title="sampleAddrText(r)">🧪 寄客户: {{ sampleAddrText(r) || '地址见其他要求(기타요청사항 참조)' }}</div></template></template>
                      </td>
                    </template>
                  </tr>
                  </template>
                  <tr v-if="!visibleRows.length">
                    <td :colspan="headers.length + 10" class="text-center text-muted py-4">没有数据(데이터가 없습니다)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import axios from 'axios';
import * as XLSX from 'xlsx';
import LocationLabelScan from './LocationLabelScan';
import CoupangInboundModal from './CoupangInboundModal';
import {
  customsTypeText, CONFIRM_STATUS_OPTIONS, confirmStatusText, staffLabel,
  outboundTypeOfBox, outboundTypeInfo, outboundTypeStyle, OUTBOUND_TYPE_STYLE
} from '@/utils/coupassStatusText';

// 창고 출고리스트 엑셀 양식 그대로(문자·순서·줄바꿈 포함). 바꾸지 말 것 — 창고가 이 파일을 그대로 쓴다.
// 예외: 「주문수량」→「구매수량」(= 구매요청수량 × 판매구성수량 P, 2026-10-04 YZY 요청)
//       「物流信息」→「입고예정일&입고센터」(2026-10-06 YZY) — 원래도 쿠팡 발주의 입고예정일·발주센터 칸이었다
const HEADERS = [
  '주문날짜', '快递物流单号', '상품URL', '이미지URL', '상품이미지', '옵션/칼라/사이즈/판매구성', '바코드번호', '상품명', 'sku id',
  '구매수량', '출고수량', '업체', '판매구성&작업요청사항', '입고예정일&입고센터', '箱子大小',
  '清关方式（综合/单独）\n需要单独清关的会显示', '박스번호', '파렛트번호', '비고', '주문번호', '영어품명', 'HS코드', '가격USD',
  '基本检查', '贴条码', '贴产地标', '缝标', '盖章', '挂吊牌', '气泡袋打包',
  '缝标+盖章+挂吊牌+气泡袋打包+其他特殊操作\n所花费总时长', '作业时间', '下单账号', '입고ID', 'BL번호'
];

// 待客户确认 화면·엑셀의 열 순서(2026-10-04 YZY). 앞 7개는 엑셀 양식 칸을 그대로 쓰고(col),
// 목록에 없는 실제 추가작업·확인사진은 맨 뒤에 둔다. 나머지 엑셀 양식 열은 이 화면에서 숨긴다
const CONFIRM_COLS = [
  { key: 'company', label: '업체', col: '업체' },
  { key: 'img', label: '상품이미지', col: '상품이미지' },
  { key: 'option', label: '옵션/칼라/사이즈/판매구성', col: '옵션/칼라/사이즈/판매구성' },
  { key: 'barcode', label: '바코드번호', col: '바코드번호' },
  { key: 'name', label: '상품명', col: '상품명' },
  { key: 'qty', label: '구매수량', col: '구매수량' },
  { key: 'req', label: '판매구성&작업요청사항', col: '판매구성&작업요청사항' },
  { key: 'sentBy', label: '作业人(작업자)' },
  { key: 'memo', label: '需跟客户确认(고객확인사항)' },
  { key: 'memoKo', label: '韩文翻译(한국어)' },
  { key: 'status', label: '处理状态(처리상태)' },
  { key: 'reply', label: '客户回复处理方式(고객 회신 처리방법)' },
  { key: 'handler', label: '处理人(처리인)' },
  { key: 'actual', label: '实际追加作业(실제 추가작업)' },
  { key: 'photo', label: '确认照片(확인 사진)' }
];

// 통관방법 → 청관방식. 통합도 표시한다(2026-10-03 YZY: 비어 있는 칸이 많다 — 95% 가 통합통관이라 비워 두던 것)
// 라벨은 utils/coupassStatusText CUSTOMS_TYPE_TEXT 한 곳(托盘标签生成·货主库存出库와 같은 표기, 2026-10 YZY)
const CUSTOMS_TEXT = { client: customsTypeText('client'), kupass: customsTypeText('kupass'), integrated: customsTypeText('integrated') };
// 요청내역.customsType 이 없는 옛 건은 최상위 통관방법(한글)으로 대신한다
const CUSTOMS_BY_KO = { 화주단독통관: 'client', 쿠패스단독통관: 'kupass', 통합통관: 'integrated' };

// 2단 분류 순서(2026-10-04 YZY): 清关方式 → 出库形式(테이프 5종, OUTBOUND_TYPE_STYLE.order). 어느 쪽에도 안 맞으면 其他
const OTHER = 'other';
const OTHER_TEXT = '其他(기타)';
const CUSTOMS_ORDER = ['integrated', 'kupass', 'client', OTHER];
const OUTBOUND_ORDER = Object.keys(OUTBOUND_TYPE_STYLE)
  .sort((a, b) => OUTBOUND_TYPE_STYLE[a].order - OUTBOUND_TYPE_STYLE[b].order).concat(OTHER);

// 가격USD = 1688 낱개 위안 단가 ÷ 이 환율(2026-10-03 YZY). 바꿀 때는 여기 한 곳만 고친다.
const CNY_PER_USD = 7.4;

export default {
  name: 'LogisInboundListV0',
  components: { LocationLabelScan, CoupangInboundModal },
  data() {
    return {
      pageTitle: coupassMenuTitle(this.$route.name) || coupassMenuTitle('logis.logisinboundlist'),
      headers: HEADERS,
      IMG_COL: HEADERS.indexOf('상품이미지'),
      IMG_URL_COL: HEADERS.indexOf('이미지URL'),
      URL_COL: HEADERS.indexOf('상품URL'),
      confirmCols: CONFIRM_COLS,
      COL: {
        업체: HEADERS.indexOf('업체'), 옵션: HEADERS.indexOf('옵션/칼라/사이즈/판매구성'), 바코드: HEADERS.indexOf('바코드번호'),
        상품명: HEADERS.indexOf('상품명'), 구매수량: HEADERS.indexOf('구매수량'), 작업요청: HEADERS.indexOf('판매구성&작업요청사항')
      },
      // 待客户确认 탭은 사이드바 별도 메뉴(logis.logisinboundconfirm)로 꺼냈다 — 상태는 라우트 meta 가 정한다(2026-10-04 YZY)
      status: this.$route.meta.inboundStatus || '출고대기',
      me: '', mineOnly: false, mineInit: false, // 待客户确认 «내가 보낸 건만»(#48)
      focusId: '', // 알림을 눌러 들어온 건(2026-10-16 SYL #38) — 그 줄을 노랗게 하고 화면에 보이게 한다
      boundRoute: this.$route.name,
      startdate: '',
      enddate: '',
      companyid: '',
      companyOptions: [],
      confirmedFixed: {}, // 韩文标签资料 확정 申报金额(2026-10-16)
      confirmedNames: {}, // 확정 영문 품명
      rows: [],
      confirmStatusOptions: CONFIRM_STATUS_OPTIONS,
      OTHER,
      customsFilter: '',
      outboundFilter: '',
      showCoupangInbound: false,
      loading: false
    };
  },
  computed: {
    isConfirm() {
      return this.status === '출고확인대기';
    },
    selectable() {
      return this.status !== '출고대기';
    },
    // 선택·일괄작업·엑셀은 지금 보이는(분류로 고른) 건만 대상이다
    selectedIds() {
      return this.visibleRows.filter(r => r._selected).map(r => r._id);
    },
    allChecked() {
      return this.visibleRows.length > 0 && this.visibleRows.every(r => r._selected);
    },
    // 待客户确认 «내가 보낸 건만» — 내가 작업자(待客户确认 으로 보낸 직원)인 건. 다른 화면(입고리스트 등)에는 적용 안 함
    scopedRows() {
      return this.isConfirm && this.mineOnly && this.me ? this.rows.filter(r => r._sentBy === this.me) : this.rows;
    },
    mineCount() {
      return this.me ? this.rows.filter(r => r._sentBy === this.me).length : 0;
    },
    customsGroups() {
      return CUSTOMS_ORDER.map(key => ({
        key, text: key === OTHER ? OTHER_TEXT : CUSTOMS_TEXT[key], count: this.scopedRows.filter(r => r._customsKey === key).length
      })).filter(g => g.count || g.key === this.customsFilter);
    },
    customsRows() {
      return this.customsFilter ? this.scopedRows.filter(r => r._customsKey === this.customsFilter) : this.scopedRows;
    },
    outboundGroups() {
      return OUTBOUND_ORDER.map(key => ({
        key, text: key === OTHER ? OTHER_TEXT : OUTBOUND_TYPE_STYLE[key].text, count: this.customsRows.filter(r => r._outKey === key).length
      })).filter(g => g.count || g.key === this.outboundFilter);
    },
    // 清关方式 → 出库形式 순으로 묶는다(같은 묶음 안은 원래 순서 = 입고일 최신순)
    groupedRows() {
      const list = this.outboundFilter ? this.customsRows.filter(r => r._outKey === this.outboundFilter) : this.customsRows;
      const map = {};
      list.forEach(r => {
        const k = r._customsKey + '|' + r._outKey;
        (map[k] = map[k] || []).push(r);
      });
      const groups = [];
      CUSTOMS_ORDER.forEach(c => OUTBOUND_ORDER.forEach(o => {
        const rows = map[c + '|' + o];
        if (!rows) return;
        groups.push({
          key: c + '|' + o, rows, outKey: o,
          customsText: c === OTHER ? OTHER_TEXT : CUSTOMS_TEXT[c],
          outText: o === OTHER ? OTHER_TEXT : OUTBOUND_TYPE_STYLE[o].text
        });
      }));
      // 묶음이 둘 이상일 때만 머리말을 단다(한 묶음만 고른 상태면 칩이 이미 말해 준다)
      groups.forEach(g => { g.head = groups.length > 1; });
      return groups;
    },
    visibleRows() {
      return this.groupedRows.reduce((a, g) => a.concat(g.rows), []);
    }
  },
  watch: {
    // 알림(客户已回复)을 눌러 같은 화면이 이미 열려 있을 때 — 새로 찾은 뒤 그 줄로 간다
    '$route.query.focus'(v) {
      if (!v || this.$route.name !== this.boundRoute) return;
      this.focusId = String(v);
      this.search();
    },
    // 入库列表 ↔ 待客户确认 메뉴를 오가면 같은 화면이 재사용된다 — 상태·제목을 다시 잡고 다시 찾는다
    '$route.name'() {
      // 상단 탭(keep-alive)에서는 入库列表·待客户确认 이 라우트마다 화면을 따로 갖는다 —
      // 숨어 있는 탭이 다른 메뉴 주소를 보고 자기 상태를 바꾸지 않게, 처음 열린 메뉴가 아니면 무시한다
      if (this.$route.name !== this.boundRoute) return;
      this.status = this.$route.meta.inboundStatus || '출고대기';
      this.pageTitle = coupassMenuTitle(this.$route.name) || coupassMenuTitle('logis.logisinboundlist');
      this.rows = [];
      this.search();
    }
  },
  mounted() {
    this.search();
  },
  methods: {
    // 샘플검증 → 화주에게 발송 건(2026-10-04 YZY): 화주주소·신고영문명·HS 를 창고에서도 본다.
    // 샘플검증 화면 저장 직후엔 sampleCheck 가 없고 reqtype '샘플검증' 이다. 폐기(destroy·distroy)는 제외.
    isSampleSend(r) {
      if (!r) return false;
      const 요청 = r.요청내역 || {};
      const sc = 요청.sampleCheck;
      const sample = r.reqtype === '샘플검증' || !!(sc && sc.enabled);
      if (!sample || 요청.destination === 'distroy') return false;
      return !(sc && sc.enabled && sc.disposition === 'destroy');
    },
    sampleAddrText(r) {
      const a = (r && r.요청내역 && r.요청내역.화주주소) || {};
      return [a.업체명, a.주소, a.연락처].map(v => (v || '').trim()).filter(Boolean).join(' / ');
    },
    staffLabel,
    outboundTypeStyle,
    setCustoms(key) {
      this.customsFilter = key;
      this.outboundFilter = '';
    },
    fmtTime(v) {
      const d = v ? new Date(v) : null;
      if (!d || isNaN(d.getTime())) return '';
      return (d.getMonth() + 1) + '/' + d.getDate() + ' ' + d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0');
    },
    confirmDirty(r) {
      const 요청 = r.요청내역 || {};
      return r._confirmStatus !== (요청.confirmStatus || '확인중') || r._confirmReply !== (요청.confirmReply || '')
        || !요청.confirmStatus; // 한 번도 저장 안 한 건은 기본값 그대로도 저장할 수 있다
    },
    // 处理状态·客户回复处理方式 저장 — 处理人(이름·代号)은 서버가 로그인한 직원으로 남긴다.
    // statusOnly = 处理状态 드롭다운을 고른 즉시 저장(2026-10-04 YZY). 이때 회신 칸은 저장된 값을 그대로 보내
    // 아직 保存 안 누른 회신 입력이 같이 저장되지 않게 한다(회신은 保存 버튼으로만)
    async saveConfirm(r, statusOnly) {
      const 요청 = r.요청내역 || {};
      const prevStatus = 요청.confirmStatus || '확인중';
      const reply = statusOnly ? (요청.confirmReply || '') : r._confirmReply;
      r._confirmBusy = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/confirmstate', {
          _id: r._id, confirmStatus: r._confirmStatus, confirmReply: reply
        });
        if (data.요청결과 !== '성공') {
          console.error('처리상태 저장 실패:', data);
          if (statusOnly) r._confirmStatus = prevStatus;
          alert('保存失败(저장 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        if (!r.요청내역) this.$set(r, '요청내역', {});
        ['confirmHandledBy', 'confirmHandledByCode', 'confirmHandledAt'].forEach(k => this.$set(r.요청내역, k, data[k]));
        this.$set(r.요청내역, 'confirmStatus', r._confirmStatus);
        this.$set(r.요청내역, 'confirmReply', reply);
        this.$bvToast.toast(r._confirmStatus === '처리완료'
          ? '已保存 — 已显示在「已跟客户确认」(저장 — 已跟客户确认 탭에도 보입니다)' : '已保存(저장했습니다)',
        { title: '保存(저장)', variant: 'success', solid: true, autoHideDelay: 2000 });
      } catch (e) {
        console.error('처리상태 저장 오류:', e);
        if (statusOnly) r._confirmStatus = prevStatus;
        alert('保存错误(저장 오류): ' + e.message);
      } finally {
        r._confirmBusy = false;
      }
    },
    // 需跟客户确认(중국어) → 화주에게 보낼 한국어 안내 문안. 서버 번역 API(target 'ko-notice')를 쓰고, 결과는 workMemo_ko 에 저장한다
    async makeNotice(r) {
      const 요청 = r.요청내역 || {};
      if (!요청.workMemo || !요청.workMemo.trim()) return;
      this.$set(r, '_noticeBusy', true);
      try {
        const context = [
          r.상품명 ? '상품명: ' + r.상품명 : '',
          [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).length ? '옵션: ' + [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' / ') : '',
          r.바코드 ? '바코드: ' + r.바코드 : '',
          r.입고수량 ? '입고수량: ' + r.입고수량 : '',
          요청.workMemoActual ? '창고가 실제 진행한 추가작업: ' + 요청.workMemoActual : ''
        ].filter(Boolean).join('\n');
        const { data } = await axios.post('/api/ai/translate-to-chinese', {
          target: 'ko-notice',
          texts: [{ key: 'notice', text: 요청.workMemo, context }]
        });
        const text = data && data.success && data.results && data.results[0] ? (data.results[0].translated || '') : '';
        if (!text.trim()) {
          alert((data && data.error) || '生成失败(문안 생성 실패)');
          return;
        }
        const save = await axios.post('/api/reqpurchase/updatereqcontent', { _id: r._id, field: 'workMemo_ko', value: text });
        if (!save.data || !save.data.success) {
          console.error('안내 문안 저장 실패:', save.data);
          alert('保存失败(저장 실패) — 文案只显示在本页(문안은 이 화면에만 보입니다)');
        }
        if (!r.요청내역) this.$set(r, '요청내역', {});
        this.$set(r.요청내역, 'workMemo_ko', text);
      } catch (e) {
        console.error('안내 문안 생성 오류:', e);
        alert('生成失败(문안 생성 실패): ' + e.message);
      } finally {
        this.$set(r, '_noticeBusy', false);
      }
    },
    async copyNotice(r) {
      const text = (r.요청내역 && r.요청내역.workMemo_ko) || '';
      try {
        await navigator.clipboard.writeText(text);
        this.$bvToast.toast('已复制(복사했습니다)', { title: '复制(복사)', variant: 'success', solid: true, autoHideDelay: 1500 });
      } catch (e) {
        // http 화면 등 clipboard 가 막힌 곳 — 직접 복사할 수 있게 띄운다
        console.error('문안 복사 오류:', e);
        window.prompt('Ctrl+C 复制(복사하세요)', text);
      }
    },
    // 韩文标签资料 의 확정 申报金额·확정 영문 품명(「화주|바코드」, 2026-10-16 YZY) — 출고자료 엑셀의 영어품명·가격USD 에 우선 적용한다(箱单发票 와 같은 값).
    // 못 읽으면 조용히 넘기지 않고 알린다(틀린 申报单价가 창고로 나가는 것을 막는다)
    async loadConfirmed(rows) {
      const items = new Map();
      rows.forEach(r => { const bc = String(r.바코드 || '').replace(/\s+/g, ''); if (r.companyid && bc) items.set(r.companyid + '|' + bc, { companyid: r.companyid, barcode: bc }); });
      this.confirmedFixed = {}; this.confirmedNames = {};
      if (!items.size) return;
      try {
        const { data } = await axios.post('/api/coupassLabel/fixedPrices', { items: [...items.values()] });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '조회 실패');
        this.confirmedFixed = data.map || {}; this.confirmedNames = data.names || {};
      } catch (e) {
        console.error('확정 申报金额·영문 품명 읽기 실패:', e);
        alert('固定申报金额/确认英文品名读取失败，出库资料的申报单价可能不是最新(확정 申报金额·영문 품명을 읽지 못해 출고자료 값이 최신이 아닐 수 있습니다): ' + e.message);
      }
    },
    confirmedKey(r) { return r.companyid + '|' + String(r.바코드 || '').replace(/\s+/g, ''); },
    toggleAll(v) {
      this.rows.forEach(r => { r._selected = false; });
      this.visibleRows.forEach(r => { r._selected = v; });
    },
    // 알림으로 들어온 건 줄로 스크롤(없으면 그냥 둔다 — 상태·필터에 걸려 안 보일 수 있다)
    scrollToFocus() {
      const f = this.focusId || (this.$route.name === this.boundRoute ? String(this.$route.query.focus || '') : '');
      if (!f) return;
      this.focusId = f;
      this.$nextTick(() => {
        const el = document.getElementById('confirm-row-' + f);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    async search() {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/outlist', {
          status: this.status, companyid: this.companyid, start: this.startdate, end: this.enddate
        });
        if (data.요청결과 !== '성공') {
          alert('搜索失败(검색 실패): ' + ((data.error && data.error.errorMessage) || ''));
          this.rows = [];
          return;
        }
        if (data.truncated) alert('结果过多，仅显示前3000条，请缩小日期范围(결과가 많아 3000건만 표시합니다. 날짜를 좁혀주세요)');
        await this.loadConfirmed(data.rows || []);
        this.me = data.me || '';
        this.rows = (data.rows || []).map(r => ({
          ...r,
          _selected: false,
          _위치번호: r.제품위치 || '',
          _confirmStatus: (r.요청내역 && r.요청내역.confirmStatus) || '확인중',
          _confirmReply: (r.요청내역 && r.요청내역.confirmReply) || '',
          _confirmBusy: false,
          _customsKey: this.customsKey(r),
          // 박스별 发货方式(箱子分配에서 바꾼 값, r._box)이 있으면 그 박스 건은 그걸로 분류한다
          _outKey: (outboundTypeInfo(outboundTypeOfBox(r._box, r)) || {}).key || OTHER,
          _cells: this.toCells(r)
        }));
        const set = new Set(this.companyOptions);
        this.rows.forEach(r => { if (r.companyid) set.add(r.companyid); });
        this.companyOptions = [...set].sort();
        // 처음 열 때만 기본값을 정한다 — 내가 보낸 건이 있는 직원 계정은 켜고, 없으면(관리자 등) 끈다. 이후엔 직원이 고른 대로 둔다
        if (!this.mineInit && this.isConfirm) { this.mineInit = true; this.mineOnly = this.mineCount > 0; }
        this.scrollToFocus();
      } catch (e) {
        console.error('입고리스트 검색 오류:', e);
        alert('搜索错误(검색 오류): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    customsKey(r) {
      const k = (r.요청내역 && r.요청내역.customsType) || CUSTOMS_BY_KO[r.통관방법];
      return CUSTOMS_TEXT[k] ? k : OTHER;
    },
    ymd(v) {
      if (!v) return '';
      const d = new Date(typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v) : v);
      if (isNaN(d.getTime())) return String(v).slice(0, 10);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    // 2026-10-08 → 2026/10/8 (양식 표기)
    slashDate(v) {
      const s = this.ymd(v);
      if (!s) return '';
      const p = s.split('-');
      return p.length === 3 ? p[0] + '/' + parseInt(p[1]) + '/' + parseInt(p[2]) : s;
    },
    // 2026/9/28--9:22 (양식 표기)
    workTime(ms) {
      if (!ms) return '';
      const d = new Date(ms);
      if (isNaN(d.getTime())) return '';
      return d.getFullYear() + '/' + (d.getMonth() + 1) + '/' + d.getDate() + '--' + d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0');
    },
    boxAddressNote(box) {
      if (!box || box.shipMethodOverride !== 'customer_address') return '';
      const a = box.customerAddress || {};
      // 주소를 못 찾아 待客户确认 으로 보낸 박스는 주소 없이 저장된다(2026-10-04 YZY 19:36)
      return '货主地址发货: ' + ([a.업체명, a.주소, a.연락처].filter(Boolean).join(' / ') || '地址待客户确认');
    },
    toCells(r) {
      const 요청 = r.요청내역 || {};
      const 물류 = r.물류정보 || {};
      const 운송장 = (Array.isArray(물류.운송장번호목록) && 물류.운송장번호목록.length)
        ? 물류.운송장번호목록.join('\n') : (물류.운송장번호 || '');
      // 선택한 발주(selectpo)의 입고예정일·발주센터 — 서버가 _po 로 골라 준다(주문내역 엑셀과 같은 규칙)
      const po = r._po || null;
      const box = r._box || {};
      // 발주가 없는 로켓그로스 밀크런 박스는 「쿠팡 입고자료」 창에서 박스에 저장한 입고예정일·입고센터(예 2026/10/8 경기광주5(19))
      const 물류정보 = po ? [this.slashDate(po.입고예정일), po.발주센터 || ''].filter(Boolean).join('\n')
        : [this.slashDate(box.coupangInboundDate), box.coupangInboundCenter || ''].filter(Boolean).join(' ');
      const 출고 = r.출고정보 || {};
      const ok = v => (v ? 'OK' : '');
      // 구매수량 = 구매요청수량 × 판매구성수량(P). 판매구성수량이 없거나 단품이면 1 (2026-10-04 YZY)
      const 요청수량 = parseInt(r.원구매요청수량 != null ? r.원구매요청수량 : r.구매요청수량);
      const 구매수량 = 요청수량 ? 요청수량 * this.composeN(r.판매구성수량) : '';
      return [
        this.ymd(r.요청일),
        운송장,
        r.구매링크 || '',
        r.등록이미지 || '',
        '',                                   // 상품이미지: 엑셀 DISPIMG 불가 — 비움
        [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' '),
        r.바코드 || '',
        r.상품명 || '',
        r.SKUID || '',
        구매수량,
        parseInt(r.입고수량) || parseInt(r.구매요청수량) || '',
        r.companyid || '',
        요청.reqcontent || '',
        물류정보,
        box.박스크기 || '',
        CUSTOMS_TEXT[요청.customsType || CUSTOMS_BY_KO[r.통관방법]] || '',
        출고.boxLabelId || '',
        출고.palletLabelId || box.palletLabelId || '',
        this.boxAddressNote(box),             // 비고 — 화주 주소 발송 박스면 그 주소(箱子分配에서 입력)
        (r.주문정보 && r.주문정보['1688주문번호']) || '',
        this.confirmedNames[this.confirmedKey(r)] || r.신고영문명 || '',
        r.HSCODE || '',
        this.confirmedFixed[this.confirmedKey(r)] > 0 ? this.confirmedFixed[this.confirmedKey(r)] : this.priceUsd(r),
        ok(요청.default), ok(요청.barcode), ok(요청.sticker), ok(요청.sewing), ok(요청.stamp), ok(요청.tag), ok(요청.airpacking),
        '',                                   // 작업 소요시간: 창고 기입
        this.workTime(r.입고날짜),
        '',                                   // 下单账号: 저장된 필드 없음
        box.coupangInboundId || '',           // 입고ID — 쿠팡 입고자료 창에서 박스에 저장(Labels.coupangInboundId)
        ''                                    // BL번호
      ];
    },
    // 가격USD(2026-10-03 YZY): 단품 = 낱개 위안 ÷ 7.4 / N개 묶음(판매구성수량) = 낱개 위안 × N ÷ 7.4
    // 합포장(묶음구성) = Σ(구성 상품 낱개 위안 × 그 구성의 N) ÷ 7.4 (YZY: A 2p + B 2p → (A×2 + B×2)/7.4)
    // 구성 하나라도 단가가 없거나 구성 수가 모자라면 비운다(덜 신고 방지). 계산이 안 되면 예전처럼 신고가격을 그대로 쓴다.
    composeN(v) {
      return v === '단품' || !v ? 1 : (parseInt(v) || 1);
    },
    priceUsd(r) {
      let cny = null;
      if (r.묶음구성여부 === true) {
        const parts = Array.isArray(r._bundleParts) ? r._bundleParts : [];
        const need = parseInt(r.묶음구성수량) || parts.length;
        if (parts.length >= need && parts.length && parts.every(p => p.cny > 0)) {
          cny = parts.reduce((s, p) => s + p.cny * this.composeN(p.n), 0);
        }
      } else if (r._unitCny > 0) {
        cny = r._unitCny * this.composeN(r.판매구성수량);
      }
      if (cny != null) return Math.round(cny / CNY_PER_USD * 100) / 100;
      return r.신고가격 != null && r.신고가격 !== '' && r.신고가격 !== 0 ? r.신고가격 : '';
    },
    // 待客户确认 엑셀 = 화면과 같은 열 순서(2026-10-04 YZY). 상품이미지는 엑셀에 그림을 못 넣어 이미지 주소를 쓴다
    confirmExcelRow(r) {
      const 요청 = r.요청내역 || {};
      const c = r._cells;
      const v = {
        sentBy: staffLabel(r._sentBy, r._sentByCode),
        memo: 요청.workMemo || '',
        memoKo: 요청.workMemo_ko || '',
        status: confirmStatusText(요청.confirmStatus),
        reply: 요청.confirmReply || '',
        handler: [staffLabel(요청.confirmHandledBy, 요청.confirmHandledByCode), this.fmtTime(요청.confirmHandledAt)].filter(Boolean).join(' '),
        actual: 요청.workMemoActual || '',
        photo: (r._확인사진 || []).join('\n')
      };
      return CONFIRM_COLS.map(col => (col.key === 'img' ? c[this.IMG_URL_COL] : col.col ? c[HEADERS.indexOf(col.col)] : v[col.key]));
    },
    downloadExcel() {
      if (this.isConfirm) {
        const ws = XLSX.utils.aoa_to_sheet([CONFIRM_COLS.map(col => col.label)].concat(this.visibleRows.map(r => this.confirmExcelRow(r))));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const d = new Date();
        const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
        XLSX.writeFile(wb, '待客户确认_' + stamp + '.xlsx');
        return;
      }
      const aoa = [HEADERS].concat(this.visibleRows.map(r => r._cells));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // 상품URL·이미지URL 칸은 주소 전체를 그대로 쓰고 하이퍼링크도 건다(1행은 헤더).
      // 화면 표는 「link」 글자, 엑셀은 완전한 URL — 창고가 주소를 복사해 쓴다(2026-10-03 YZY)
      this.visibleRows.forEach((r, i) => {
        [this.URL_COL, this.IMG_URL_COL].forEach(c => {
          const url = r._cells[c];
          const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c })];
          if (url && cell) cell.l = { Target: url, Tooltip: url };
        });
      });
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const d = new Date();
      const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      XLSX.writeFile(wb, '入库列表_' + this.status + '_' + stamp + '.xlsx');
    },
    // 출고확인대기 → 출고대기
    async confirmOutbound() {
      const ids = this.selectedIds;
      if (!ids.length) return;
      if (!confirm('确认出库 ' + ids.length + '件?(' + ids.length + '건을 출고대기로 넘기시겠습니까?)')) return;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updateProcessStatusBulk', {
          ids, toStatus: '출고대기', memo: '출고확인 (입고리스트)'
        });
        if (data.요청결과 !== '성공') {
          alert('处理失败(처리 실패): ' + (data.error || ''));
        } else if (data.skippedCount) {
          // 서버가 건너뛴 건은 반드시 알린다 — 성공처럼 보이고 DB 는 그대로인 상태를 막는다
          alert('完成 ' + data.successCount + '件, 跳过 ' + data.skippedCount + '件(건너뜀): ' + data.skipped.map(s => s.reason).join(', '));
        } else {
          this.$bvToast.toast(data.successCount + '件 → 入库列表(입고리스트)', { title: '完成(완료)', variant: 'success', solid: true });
        }
      } catch (e) {
        console.error('출고확인 오류:', e);
        alert('处理错误(처리 오류): ' + e.message);
      } finally {
        this.loading = false;
        this.search();
      }
    },
    confirmStockSelected() {
      this.confirmStock(this.rows.filter(r => r._selected));
    },
    // 재고대기 → 중국재고 (위치번호 확정)
    async confirmStock(list) {
      const items = list.map(r => ({ _id: r._id, 위치번호: r._위치번호 }));
      const empty = items.filter(i => !i.위치번호);
      if (empty.length) return alert('请输入库位号(위치번호를 입력해주세요) — ' + empty.length + '件');
      if (!items.length) return;
      this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/confirmStock', { items });
        if (data.요청결과 !== '성공') {
          alert('处理失败(처리 실패): ' + ((data.error && data.error.errorMessage) || ''));
        } else if (data.skipped && data.skipped.length) {
          alert('完成 ' + data.results.length + '件, 跳过 ' + data.skipped.length + '件(건너뜀): ' + data.skipped.map(s => s.reason).join(', '));
        } else {
          this.$bvToast.toast(data.results.length + '件 → 中国库存(중국재고)', { title: '完成(완료)', variant: 'success', solid: true });
        }
      } catch (e) {
        console.error('재고확정 오류:', e);
        alert('处理错误(처리 오류): ' + e.message);
      } finally {
        this.loading = false;
        this.search();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// 행·제목 높이를 낮추고(한 줄 위주) 세로 스크롤 때 제목 행을 고정한다(2026-10-03 YZY)
.inbound-list {
  max-height: 75vh;
  overflow: auto;

  // 공통 스타일이 table 에 overflow:hidden 을 걸어 sticky 가 안 먹는다 — 여기서 풀어 준다.
  // 칸을 화면 폭에 욱여넣으면 글자가 한 자씩 꺾여 행이 수백 px 가 되므로 표는 내용 폭(가로 스크롤)으로 둔다
  table {
    overflow: visible;
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #e9ecef;
    box-shadow: inset 0 -1px 0 #dee2e6;
  }

  th,
  td {
    padding: 2px 4px !important; // 테마의 .table td { padding: 15px 20px !important } 를 이긴다
    line-height: 1.25;
    vertical-align: middle;
  }

  &__th {
    white-space: nowrap;
    font-size: 0.72rem;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__td {
    white-space: pre-line;
    font-size: 0.72rem;
    max-width: 220px;
    overflow-wrap: anywhere;
  }

  // 화주에게 보낼 안내 문안 칸 — 복사해 쓰므로 넓게, 줄바꿈 그대로
  &__notice {
    min-width: 260px;
    max-width: 360px;
  }

  &__reply {
    min-width: 220px;
  }

  &__notice-text {
    white-space: pre-wrap;
    user-select: all;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 3px;
    padding: 2px 4px;
  }

  &__group td {
    background: #f1f3f5;
    font-weight: 600;
    font-size: 0.78rem;
    padding: 4px 6px !important;
  }

  &__img {
    width: 32px;
    height: 32px;
    object-fit: cover;
    display: block;
  }
}
</style>
